'use client';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { CarouselContent, CarouselItem, CarouselNew } from '@ui/CarouselNew';
import SafeImage from '@ui/SafeImage';
import { prefix } from '@util/imagePrefix';
import type { UseEmblaCarouselType } from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';

type CarouselApi = UseEmblaCarouselType[1];

const images = [
  `${prefix}/assets/adolf-silva-giveaway/carousel-image-1_1920x2560.png`,
  `${prefix}/assets/adolf-silva-giveaway/carousel-image-2_1920x2560.png`,
  `${prefix}/assets/adolf-silva-giveaway/carousel-image-3_1920x2560.png`,
];

export default function AdolfPicCarousel() {
  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, [api]);

  useEffect(() => {
    if (!api) return;
    onSelect();
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api, onSelect]);

  return (
    <div className="relative w-full py-16 sm:py-24">
      <CarouselNew
        opts={{
          align: 'center',
          loop: true,
          skipSnaps: false,
          slidesToScroll: 1,
        }}
        setApi={setApi}
        className="w-full"
      >
        <CarouselContent className="!pb-0 !pt-0">
          {images.map((src, index) => (
            <CarouselItem
              key={index}
              className="basis-[85%] px-4 sm:basis-[45%] sm:px-6"
            >
              <SafeImage
                src={src}
                alt={`Adolf Silva ${index + 1}`}
                className="h-[28rem] w-full rounded-2xl object-cover sm:h-[45rem]"
                width={1920}
                height={2560}
              />
            </CarouselItem>
          ))}
        </CarouselContent>

        {canScrollPrev && (
          <button
            type="button"
            aria-label="Previous slide"
            onClick={() => api?.scrollPrev()}
            className="absolute left-8 top-1/2 z-10 flex h-16 w-16 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md sm:left-24"
          >
            <ChevronLeftIcon className="h-10 w-10 text-black" />
          </button>
        )}

        {canScrollNext && (
          <button
            type="button"
            aria-label="Next slide"
            onClick={() => api?.scrollNext()}
            className="absolute right-8 top-1/2 z-10 flex h-16 w-16 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md sm:right-24"
          >
            <ChevronRightIcon className="h-10 w-10 text-black" />
          </button>
        )}
      </CarouselNew>
    </div>
  );
}
